import { useState, useEffect } from "react";
import { APP_BASE_URL } from "@/config/env";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Lock, Mail, ArrowLeft } from "lucide-react";

const LOGO_DARK = "https://kargonode.com/wp-content/uploads/2026/02/logo-correo.webp";
const LOGO_LIGHT = "https://kargonode.com/wp-content/uploads/2026/03/Manual-de-Marca-HUMAN-CLARITY-Pilar-Rodriguez-9.webp";

function useIsDark() {
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains("dark"));
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);
  return isDark;
}

const Login = () => {
  const { login } = useAuth();
  const isDark = useIsDark();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotMessage, setForgotMessage] = useState("");
  const successMessage = (location.state as any)?.message as string | undefined;

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const err = await login(email, password);
    if (err) setError("Credenciales inválidas.");
    setLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setForgotMessage("");
    setLoading(true);

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${APP_BASE_URL}/reset-password`,
    });

    if (error) {
      setError("No se pudo enviar el correo. Verifica tu email.");
    } else {
      setForgotMessage("Se envió un enlace de recuperación a tu correo.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background grid-pattern relative overflow-hidden">
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-primary/5 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-secondary/10 blur-[100px] pointer-events-none" />

      <div className="w-full max-w-md px-6 animate-fade-in">
        <div className="glass rounded-2xl p-8 glow-cyan">
          <div className="flex justify-center mb-8">
            <img src={isDark ? LOGO_DARK : LOGO_LIGHT} alt="KargoNode" className="h-10 object-contain" />
          </div>

          <h2 className="text-lg font-semibold text-foreground text-center mb-1">
            {forgotMode ? "Recuperar Contraseña" : "Portal de Clientes"}
          </h2>
          <p className="text-sm text-muted-foreground text-center mb-8">
            {forgotMode
              ? "Ingresa tu email y te enviaremos un enlace"
              : "Ingresa tus credenciales para acceder"}
          </p>

          <form onSubmit={forgotMode ? handleForgotPassword : handleLogin} className="space-y-5">
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="email"
                  placeholder="usuario@empresa.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-muted/50 border-border focus:border-primary focus:ring-primary"
                  required
                />
              </div>
            </div>

            {!forgotMode && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">Contraseña</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 bg-muted/50 border-border focus:border-primary focus:ring-primary"
                    required
                    minLength={6}
                  />
                </div>
              </div>
            )}

            {successMessage && !error && (
              <p className="text-sm text-primary bg-primary/10 rounded-md px-3 py-2 text-center">{successMessage}</p>
            )}
            {error && <p className="text-sm text-destructive text-center">{error}</p>}
            {forgotMessage && <p className="text-sm text-primary text-center">{forgotMessage}</p>}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold glow-cyan-strong"
            >
              {loading
                ? (forgotMode ? "Enviando..." : "Ingresando...")
                : (forgotMode ? "Enviar Enlace" : "Iniciar Sesión")}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground text-center mt-6">
            <button
              type="button"
              onClick={() => { setForgotMode(!forgotMode); setError(""); setForgotMessage(""); }}
              className="text-primary hover:underline font-medium inline-flex items-center gap-1"
            >
              {forgotMode && <ArrowLeft className="h-3 w-3" />}
              {forgotMode ? "Volver a Iniciar Sesión" : "¿Olvidaste tu contraseña?"}
            </button>
          </p>
        </div>

        <p className="text-xs text-muted-foreground text-center mt-6">
          © 2026 KargoNode · Ingeniería de Datos para Comercio Exterior
        </p>
      </div>
    </div>
  );
};

export default Login;
